/**
 * Deploy-time configuration supplied to the SPA as `window.__ENV__` by `/config.js`, which the
 * deployment mounts over the image's baked-in fallback (see docs/RUNTIME-CONFIG.md for the contract
 * the Helm chart implements). This reader coerces the raw string values to typed values with safe
 * defaults so callers never touch the global. In local dev the fallback `public/config.js` turns
 * Labs on and leaves everything else "off".
 */
export type RuntimeEnv = {
  areDashboardsEnabled: boolean
  dashboardsUrl?: string
  /** Labs, Pixee's experimental features: the in-product ones and the Forge link. */
  isLabsEnabled: boolean
  /**
   * Forge is a separate deployment this SPA does not route to, so the nav can only link to it once
   * an operator says where it is.
   */
  labsUrl?: string
  sentryDsn?: string
  /**
   * True when nothing vouched for the config the browser received, so every other field is at its
   * default regardless of how the deploy is configured. This is the difference between "the operator
   * turned everything off" and "config never arrived" — the silent failure behind ISS-8244.
   */
  isRuntimeConfigMissing: boolean
}

/**
 * Absent and empty are the same answer. A ConfigMap that interpolates a value the operator never set
 * still emits the key, carrying an empty string — indistinguishable from omitting it, and not
 * something every caller should have to handle twice.
 */
const optionalString = (value: string | undefined): string | undefined => (value !== '' ? value : undefined)

/**
 * Forge's base is its origin plus whatever `BASE_PATH` it was deployed with — `/forge` on Edge, empty
 * when Forge owns its host. That prefix is injected into Forge's router at serve time, so it belongs
 * to the deployment, not to Forge's routes: every destination path below is Forge-internal and
 * carries its own leading slash. Trim any trailing one so the two never compose into `//`.
 */
const asBaseUrl = (value: string | undefined): string | undefined => optionalString(value?.replace(/\/+$/, ''))

export function getRuntimeEnv(): RuntimeEnv {
  // Deliberately fails closed: only an explicit `true` counts as configured. An absent sentinel
  // means /config.js 404'd, was truncated, or came from an image whose deployment supplied no
  // config — all cases where the flags are meaningless rather than merely off, so every one of them
  // takes its default.
  const isRuntimeConfigMissing = window.__ENV_CONFIGURED__ !== true
  const raw = isRuntimeConfigMissing ? {} : (window.__ENV__ ?? {})
  return {
    areDashboardsEnabled: raw.DASHBOARDS_ENABLED === 'true',
    dashboardsUrl: optionalString(raw.DASHBOARDS_URL),
    isLabsEnabled: raw.LABS_ENABLED === 'true',
    labsUrl: asBaseUrl(raw.LABS_URL),
    sentryDsn: optionalString(raw.SENTRY_DSN),
    isRuntimeConfigMissing,
  }
}

/**
 * Whether this install runs Labs — Pixee's experimental features, in this app as well as in Forge.
 *
 * Read from `config.js`, never from the API, so every route guard and nav entry can decide
 * synchronously. Missing runtime config counts as off, as it does for every flag.
 */
export function isLabsEnabled(): boolean {
  return getRuntimeEnv().isLabsEnabled
}

/**
 * Surfaces missing runtime config in the browser console at boot. Without this the failure is
 * invisible from the outside: every flag simply reads as "off" and Sentry stays uninitialized, which
 * is indistinguishable from a deliberately minimal deploy. That is how ISS-8244 shipped unnoticed.
 * Sentry cannot carry this signal, since missing config is exactly the case where it has no DSN.
 */
export function warnIfRuntimeConfigMissing(): void {
  if (!getRuntimeEnv().isRuntimeConfigMissing) {
    return
  }

  console.error(
    'No deploy-time runtime config reached the browser, so error reporting, the Dashboards link, Labs ' +
      'and Pixee Beacon are off no matter how they are configured. The deployment should ' +
      'mount /config.js at /var/lib/pixee/runtime-config/config.js — in Pixee Enterprise Server that is the ' +
      'user-platform ConfigMap. See packages/user-platform/docs/RUNTIME-CONFIG.md.'
  )
}
